/*
 * ws-a11y.js
 * ---------------------------------------------------------------------------
 * Dialog semantics and keyboard containment for the theme's side panels
 * (cart drawer, search drawer, quick-view / product drawer, mobile filter
 * drawer, pickup availability, product information, add-address, shop-the-look).
 *
 * WHY THIS IS A SEPARATE FILE AND NOT AN EDIT TO app.js
 * Every one of these panels is opened and closed by code that already works:
 * CartDrawer in app.js, PredictiveSearch in predictive-search.js, QuickView in
 * app.js, the facet drawer in facets.js. Editing those means editing four
 * files' open/close paths, which is where drawer bugs come from. This file
 * touches none of them. It watches for the `active` class the existing code
 * already toggles and adds only what is missing. Delete this one file and the
 * theme behaves exactly as it did before.
 *
 * WHAT WAS MISSING, AND WHAT THIS ADDS
 *
 * 1. No dialog role. A panel that covers the page and takes focus is a modal
 *    dialog, but each one was a plain <div> (or <cart-drawer>). A screen
 *    reader user got no announcement that a dialog had opened, no name for it,
 *    and no signal that the page behind it was out of play. Fixed by setting
 *    role="dialog" on each panel, aria-labelledby pointing at the heading
 *    already in its header (aria-label for the search drawer, which has a
 *    search field there instead of a heading), and aria-modal="true" for as
 *    long as the panel is open.
 *
 *    aria-modal is set on open and removed on close rather than left in the
 *    markup, because aria-modal on a closed dialog is a claim that the rest of
 *    the page is unavailable while it is in fact the only thing available.
 *
 * 2. The close button could not be reached from the keyboard. <side-panel-close>
 *    is a custom element -- not a <button> -- with a click listener and no
 *    tabindex, so it was never in the tab order and had no accessible name;
 *    only the SVG icon was inside it. On the quick-view path app.js even calls
 *    .focus() on it, which silently did nothing. Fixed by giving each one
 *    role="button", tabindex="0", an aria-label from the theme's own
 *    accessibility.close string, and Enter/Space activation.
 *
 *    This is why it is done before the focus trap and not after: trapping
 *    focus in a dialog whose close control cannot be reached would be a
 *    keyboard trap, which is a worse failure than the one being fixed.
 *
 * 3. Focus could leave an open panel. Tab walked out of the drawer and on
 *    through the page behind it -- header, menu, product grid -- while the
 *    drawer stayed open in front of everything, so a keyboard user was moving
 *    through links they could not see. Fixed by wrapping Tab and Shift+Tab at
 *    the panel's first and last focusable element.
 *
 *    The focusable list is rebuilt on every keypress on purpose. The cart
 *    drawer replaces its own innerHTML whenever a line quantity changes, and
 *    the quick-view drawer is filled by fetch, so a list cached at open time
 *    would point at elements that no longer exist.
 *
 *    If a panel reports no focusable children at all, no trap is applied.
 *    Holding focus inside an empty panel would leave Escape as the only way
 *    out, and #Pickup-Availability really is empty until its fetch resolves.
 *
 * 4. Focus was not returned on close. The panel was hidden with focus still
 *    inside it, which drops focus to <body> -- the next Tab starts again from
 *    the top of the page, far from whatever the person had been doing. Fixed
 *    by remembering the control that opened the panel and focusing it again
 *    when the panel closes.
 *
 *    The opener is recorded on pointerdown/keydown rather than read at open
 *    time, because the existing code moves focus into the panel in the same
 *    synchronous click handler that adds the `active` class -- by the time
 *    this file sees the change, document.activeElement is already the panel.
 *
 * DELIBERATE EXCLUSIONS
 * - The mobile navigation is a native <details>, not a .side-panel. Browsers
 *   already keep the contents of a closed <details> out of the tab order and
 *   <summary> is already focusable, so it needs nothing here.
 * - The .click-capture backdrop is marked aria-hidden. It is an empty div
 *   whose only job is to catch a click outside the panel.
 * - The product.quick-view template renders a panel that is `active` from the
 *   server, because it exists to be fetched and have its contents lifted out
 *   by QuickView. It is never browsed as a page, so this file does nothing on
 *   it rather than trapping focus in a fragment.
 *
 * Labels come from window.wsA11yLabels, set in layout/theme.liquid from the
 * locale files, so they translate with the rest of the store.
 */
(function () {
  'use strict';

  if (document.body && document.body.classList.contains('template-product-quick-view')) {
    return;
  }

  var LABELS = window.wsA11yLabels || {};
  var CLOSE_LABEL = LABELS.close || 'Close';
  var SEARCH_LABEL = LABELS.search || 'Search';

  var PANEL_SELECTOR = '.side-panel';
  var CLOSE_SELECTOR = 'side-panel-close, .side-panel-close';

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]'
  ].join(',');

  /* Controls that can open a panel. Used only to remember where focus should
     go back to, so a miss costs nothing but a focus reset to the page top. */
  var TRIGGERS = 'a[href], button, summary, quick-view, [role="button"], [tabindex]';

  var titleSeq = 0;
  var lastTrigger = null;

  /* ---------------------------------------------------------------- utils */

  function isVisible(el) {
    if (!el.getClientRects().length) {
      return false;
    }
    var style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  function focusables(panel) {
    var out = [];
    var nodes = panel.querySelectorAll(FOCUSABLE);

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];

      if (el.getAttribute('tabindex') === '-1') {
        continue;
      }
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
        continue;
      }
      if (el.closest('[aria-hidden="true"]')) {
        continue;
      }
      if (!isVisible(el)) {
        continue;
      }
      out.push(el);
    }
    return out;
  }

  function panels() {
    return document.querySelectorAll(PANEL_SELECTOR);
  }

  /* The panel a keypress belongs to. Quick view opens on top of the cart
     drawer -- body carries open-quick-view while open-cart is still set --
     so it wins; otherwise the last active panel in document order does. */
  function activePanel() {
    var open = document.querySelectorAll(PANEL_SELECTOR + '.active');

    if (!open.length) {
      return null;
    }
    if (document.body.classList.contains('open-quick-view')) {
      var quick = document.getElementById('Product-Drawer');
      if (quick && quick.classList.contains('active')) {
        return quick;
      }
    }
    return open[open.length - 1];
  }

  /* ------------------------------------------------------- decorate panel */

  function decorateCloseButtons(panel) {
    var buttons = panel.querySelectorAll(CLOSE_SELECTOR);

    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];

      if (!button.hasAttribute('role')) {
        button.setAttribute('role', 'button');
      }
      if (!button.hasAttribute('tabindex')) {
        button.setAttribute('tabindex', '0');
      }
      if (!button.hasAttribute('aria-label') && !button.textContent.trim()) {
        button.setAttribute('aria-label', CLOSE_LABEL);
      }
      /* The icon inside carries no information the label does not. */
      var icon = button.querySelector('svg');
      if (icon && !icon.hasAttribute('aria-hidden')) {
        icon.setAttribute('aria-hidden', 'true');
      }
    }
  }

  function decoratePanel(panel) {
    if (!panel.hasAttribute('role')) {
      panel.setAttribute('role', 'dialog');
    }
    /* #Facet-Drawer and a few others ship without it, and the container has
       to be focusable for focus to be moved into the dialog at all. */
    if (!panel.hasAttribute('tabindex')) {
      panel.setAttribute('tabindex', '-1');
    }

    if (!panel.hasAttribute('aria-labelledby') && !panel.hasAttribute('aria-label')) {
      var heading = panel.querySelector('.side-panel-header h1, .side-panel-header h2, .side-panel-header h3, .side-panel-header h4, .side-panel-header h5, .side-panel-header h6');

      if (heading) {
        if (!heading.id) {
          titleSeq += 1;
          heading.id = 'ws-dialog-title-' + titleSeq;
        }
        panel.setAttribute('aria-labelledby', heading.id);
      } else if (panel.classList.contains('search-drawer')) {
        panel.setAttribute('aria-label', SEARCH_LABEL);
      }
    }

    decorateCloseButtons(panel);
  }

  /* --------------------------------------------------------- open / close */

  function onPanelOpened(panel) {
    panel.setAttribute('aria-modal', 'true');
    panel.wsReturnTo = lastTrigger;

    decoratePanel(panel);

    /* The cart drawer focuses itself and the search drawer focuses its
       input (at 100ms). This only steps in for panels that move focus
       nowhere, and only if the panel is still open by then. */
    window.setTimeout(function () {
      if (!panel.classList.contains('active')) {
        return;
      }
      if (panel.contains(document.activeElement)) {
        return;
      }
      try {
        panel.focus({ preventScroll: true });
      } catch (e) {
        panel.focus();
      }
    }, 150);
  }

  function onPanelClosed(panel) {
    panel.removeAttribute('aria-modal');

    var returnTo = panel.wsReturnTo;
    panel.wsReturnTo = null;

    /* Do not pull focus away from wherever the person has already moved it. */
    var current = document.activeElement;
    if (current && current !== document.body && !panel.contains(current)) {
      return;
    }

    /* Quick view closing over an open cart drawer: focus belongs back in
       the drawer that is still on screen, not on the header behind it. */
    var stillOpen = activePanel();
    if (stillOpen) {
      try {
        stillOpen.focus({ preventScroll: true });
      } catch (e) {
        stillOpen.focus();
      }
      return;
    }

    if (returnTo && document.contains(returnTo) && isVisible(returnTo)) {
      try {
        returnTo.focus({ preventScroll: true });
      } catch (e) {
        returnTo.focus();
      }
    }
  }

  /* ------------------------------------------------------------- watching */

  function watch(panel) {
    if (panel.wsWatched) {
      return;
    }
    panel.wsWatched = true;

    decoratePanel(panel);

    var wasActive = panel.classList.contains('active');

    new MutationObserver(function () {
      var isActive = panel.classList.contains('active');

      if (isActive === wasActive) {
        return;
      }
      wasActive = isActive;

      if (isActive) {
        onPanelOpened(panel);
      } else {
        onPanelClosed(panel);
      }
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });

    /* The cart drawer rewrites its own innerHTML on every quantity change
       and the quick-view drawer is filled by fetch, so the close button is
       replaced by a fresh undecorated one. Re-apply when contents change. */
    var queued = false;
    new MutationObserver(function () {
      if (queued) {
        return;
      }
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        decoratePanel(panel);
      });
    }).observe(panel, { childList: true, subtree: true });

    /* A panel already carrying .active when this runs (server-rendered) is
       given its dialog state without having focus moved into it. */
    if (wasActive) {
      panel.setAttribute('aria-modal', 'true');
    }
  }

  function scan() {
    var list = panels();
    for (var i = 0; i < list.length; i++) {
      watch(list[i]);
    }

    var backdrop = document.querySelector('.click-capture');
    if (backdrop && !backdrop.hasAttribute('aria-hidden')) {
      backdrop.setAttribute('aria-hidden', 'true');
    }
  }

  /* ---------------------------------------------------------- keyboard */

  function rememberTrigger(event) {
    var target = event.target;

    if (!target || target.nodeType !== 1) {
      return;
    }
    if (target.closest(PANEL_SELECTOR)) {
      return;
    }

    var trigger = target.closest(TRIGGERS);
    if (trigger) {
      lastTrigger = trigger;
    }
  }

  document.addEventListener('pointerdown', rememberTrigger, true);
  document.addEventListener('keydown', rememberTrigger, true);

  document.addEventListener('keydown', function (event) {
    var key = event.key;

    /* Enter / Space on the close control, which is not a real <button>. */
    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      var target = event.target;
      if (target && target.nodeType === 1 && target.closest(CLOSE_SELECTOR)) {
        event.preventDefault();
        target.closest(CLOSE_SELECTOR).click();
        return;
      }
    }

    if (key !== 'Tab' || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }

    var panel = activePanel();
    if (!panel) {
      return;
    }
    /* Catch-all: a panel inserted after the last scan is set up here, on
       the first Tab, rather than being left untrapped. */
    if (!panel.wsWatched) {
      watch(panel);
    }

    var items = focusables(panel);
    if (!items.length) {
      /* No trap rather than a trap with no way out but Escape. */
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var current = document.activeElement;

    if (!panel.contains(current)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey) {
      /* From the container itself, Shift+Tab would leave the dialog. */
      if (current === first || current === panel) {
        event.preventDefault();
        last.focus();
      }
    } else if (current === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------------------------------------------------------------- init */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  /* Sections rendered late (theme editor, app blocks) get picked up here. */
  window.addEventListener('load', scan);
  document.addEventListener('shopify:section:load', scan);
})();
